import { isValid } from "date-fns";
import isUrl from "is-url";

import type { Gist, GistInfo, GistPopulated } from "@vita-learning/types";

import { decodeHtml, EnhancedError, isHexId } from "~/shared/utils";

import { GIST_SOURCES, GIST_MIN_MAX_LENGTH } from "./constants";
import type { GistInfoForSource } from "./types";

export enum GistErrorId {
	USER = "GIST_USER",
	TITLE = "GIST_TITLE",
	DATE = "GIST_DATE",
	MAXLENGTH = "GIST_MAX_LENGTH",
	KEYWORDS = "GIST_KEYWORDS",
	SOURCE = "GIST_SOURCE",
	FILE = "GIST_FILE",
	NOTE = "GIST_NOTE",
	URL = "GIST_URL",
	CONTENT = "GIST_CONTENT",
}

const isGistSource = (source: unknown): source is Gist["source"] =>
	GIST_SOURCES.some(s => s === source);

/**
 * Asserts that the gist is a valid partial gist
 * @param {unknown} gist unknown object that should be a GistInfoForSource
 * @param {File | undefined} file optional file object when source is 'file'
 */
export function assertsValidPartialGist(
	gist: unknown,
	file?: File
): asserts gist is GistInfoForSource {
	if (!gist || typeof gist !== "object")
		throw new Error("Gist must be an object");

	if (!("id" in gist) || !isHexId(gist.id))
		throw new Error("Gist requires a valid ID");

	if (!("userId" in gist) || !isHexId(gist.userId))
		throw new Error("Gist requires a user ID");

	if (!("title" in gist) || !gist.title || typeof gist.title !== "string")
		throw new EnhancedError("Title is a required field", GistErrorId.TITLE);

	if (
		"date" in gist &&
		(typeof gist.date !== "string" || !isValid(new Date(gist.date)))
	)
		throw new EnhancedError(
			"Date string is malformed. A valid date string is required",
			GistErrorId.DATE
		);

	if (
		!("maxLength" in gist) ||
		typeof gist.maxLength !== "number" ||
		gist.maxLength < GIST_MIN_MAX_LENGTH
	)
		throw new EnhancedError(
			`Max. bullet points must be a number greater than ${GIST_MIN_MAX_LENGTH}`,
			GistErrorId.MAXLENGTH
		);

	if (
		!("keywords" in gist) ||
		!Array.isArray(gist.keywords) ||
		!gist.keywords.every(k => typeof k === "string")
	)
		throw new EnhancedError(
			"Keywords must be an array of text denoting each keyword",
			GistErrorId.KEYWORDS
		);

	if (!("source" in gist) || !isGistSource(gist.source))
		throw new EnhancedError("Invalid source", GistErrorId.SOURCE);

	if (gist.source === "file" && !file)
		throw new EnhancedError(
			"A file is required when Source is 'File'",
			GistErrorId.FILE
		);

	if (gist.source === "url") {
		if (!("url" in gist))
			throw new EnhancedError(
				"A URL is required when Source is 'URL'",
				GistErrorId.URL
			);

		if (typeof gist.url !== "string" || !isUrl(gist.url))
			throw new EnhancedError("URL must be a valid URL", GistErrorId.URL);
	}

	if (gist.source === "note") {
		if (!("noteId" in gist))
			throw new EnhancedError(
				"A note is required when Source is 'Note'",
				GistErrorId.NOTE
			);

		if (typeof gist.noteId !== "string" || !isHexId(gist.noteId))
			throw new EnhancedError("Note ID must be a valid ID", GistErrorId.NOTE);
	}
}

/**
 * Boolean form of assertsValidPartialGist
 * @param {unknown} gist
 * @param {File | undefined} file
 * @returns {}
 */
export const isValidPartialGist = (
	gist: unknown,
	file?: File
): gist is GistInfoForSource => {
	try {
		assertsValidPartialGist(gist, file);
		return true;
	} catch (_) {
		return false;
	}
};

/**
 * Asserts that the gist is a valid gist
 * @param {unknown} gist
 * @param {File | undefined} file
 * @returns
 */
export function assertsValidGist(
	gist: unknown,
	file?: File
): asserts gist is Gist | GistInfo {
	assertsValidPartialGist(gist, file);

	if (
		!("content" in gist) ||
		!Array.isArray(gist.content) ||
		!gist.content.every(
			(c: unknown) =>
				c && typeof c === "object" && "text" in c && typeof c.text === "string"
		)
	)
		throw new EnhancedError(
			"Content must be an array of strings denoting each bullet point",
			GistErrorId.CONTENT
		);

	if (!("source" in gist) || !isGistSource(gist.source))
		throw new EnhancedError("Invalid source", GistErrorId.SOURCE);

	if (gist.source === "manual") return;

	if (!("id" in gist) || !isHexId(gist.id))
		throw new Error(
			"Generated gist must have a valid ID in order to submit. Try again"
		);

	if (!("meta" in gist) || !gist.meta || typeof gist.meta !== "object")
		throw new Error("Generated gist contains malformed metadata. Try again");

	if (
		!("createdAt" in gist.meta) ||
		!("updatedAt" in gist.meta) ||
		!("version" in gist.meta)
	)
		throw new Error("Generated gist contains malformed metadata. Try again");

	if (
		typeof gist.meta.createdAt !== "string" ||
		typeof gist.meta.updatedAt !== "string" ||
		typeof gist.meta.version !== "number"
	)
		throw new Error("Generated gist contains malformed metadata. Try again");
}

/**
 *
 * @param {unknown} gist
 * @param {File | undefined} file
 * @returns
 */
export const isValidGist = (
	gist: unknown,
	file?: File
): gist is Gist | GistInfo => {
	try {
		assertsValidGist(gist, file);

		return true;
	} catch (_) {
		return false;
	}
};

export const sortGistsByCompletion = <T extends Gist | GistPopulated>(
	a: T,
	b: T
) => {
	// Incomplete items come first
	if (a.isReviewed !== b.isReviewed) return a.isReviewed ? 1 : -1;

	// Parse dates only if they are defined
	const dateA = a.date ? new Date(a.date).getTime() : NaN;
	const dateB = b.date ? new Date(b.date).getTime() : NaN;

	// If both dates are valid, sort by older dates first
	if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) return dateA - dateB;

	// If one date is undefined or invalid, place it last
	if (Number.isNaN(dateA)) return 1;
	if (Number.isNaN(dateB)) return -1;
	// If both dates are undefined, maintain relative order
	return 0;
};

export const decodeGist = <T extends Gist | GistPopulated>(gist: T): T => {
	return {
		...gist,
		content: gist.content.map(c => ({
			...c,
			text: decodeHtml(c.text),
		})),
		title: decodeHtml(gist.title),
	};
};
